// Database Analytics Provider
//
// This provider stores analytics events in a database for long-term analysis.
// It accepts all events and provides a foundation for future database integration.

import { logger } from '../../utils/core/logger';
import { canExportAnalytics } from '../consent';
import type { AnalyticsEvent } from '../types';
import { SimpleAnalyticsProvider } from './base';

export interface DatabaseConfig {
    endpoint: string;
    apiKey?: string;
    batchSize?: number;
    flushInterval?: number;
}

export class DatabaseProvider extends SimpleAnalyticsProvider {
    public readonly name = 'database';

    private config: DatabaseConfig;
    private eventBatch: AnalyticsEvent[] = [];
    private flushTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(config: DatabaseConfig, timeoutMs: number = 10000) {
        super(timeoutMs);
        this.config = {
            batchSize: 10,
            flushInterval: 30000, // 30 seconds
            ...config,
        };
    }

    public eventFilter(_event: AnalyticsEvent): boolean {
        // Accept all events for comprehensive database storage
        return true;
    }

    protected async sendEventImpl(event: AnalyticsEvent): Promise<void> {
        // Add event to batch
        this.eventBatch.push(event);

        logger.debug(
            `Database provider: Added event to batch (${this.eventBatch.length}/${this.config.batchSize})`
        );

        // Check if we should flush the batch
        if (this.eventBatch.length >= (this.config.batchSize || 10)) {
            await this.flushBatch();
        } else {
            // Set up flush timer if not already set
            this.scheduleFlush();
        }
    }

    private scheduleFlush(): void {
        if (this.flushTimer) {
            return; // Timer already scheduled
        }

        this.flushTimer = setTimeout(() => {
            this.flushBatch().catch((error) => {
                logger.error('Database provider: Scheduled flush failed:', error);
            });
        }, this.config.flushInterval || 30000);
    }

    private async flushBatch(): Promise<void> {
        if (this.eventBatch.length === 0) {
            return;
        }

        const batchToSend = this.eventBatch.filter(canExportAnalytics);
        this.eventBatch = [];

        // Clear flush timer
        if (this.flushTimer) {
            clearTimeout(this.flushTimer);
            this.flushTimer = null;
        }
        if (batchToSend.length === 0) return;

        logger.debug(`Database provider: Flushing batch of ${batchToSend.length} events`);

        try {
            await this.sendBatchToDatabase(batchToSend);
            logger.debug('Database provider: Batch sent successfully');
        } catch (error) {
            logger.error('Database provider: Failed to send batch:', error);

            // Re-add events to batch for retry
            this.eventBatch.unshift(...batchToSend.filter(canExportAnalytics));

            throw error;
        }
    }

    private async sendBatchToDatabase(events: AnalyticsEvent[]): Promise<void> {
        if (!canExportAnalytics() || !events.every(canExportAnalytics)) return;
        const payload = {
            events: events.map((event) => ({
                type: event.type,
                properties: event.properties,
                context: {
                    ...event.context,
                    // Ensure personId is included in context
                    personId: event.context.personId,
                },
                timestamp: event.timestamp,
                category: event.category,
            })),
            metadata: {
                batch_id: `batch_${Date.now()}`,
                source: 'frontend',
                version: '1.0',
            },
        };

        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        };

        if (this.config.apiKey) {
            headers.Authorization = `Bearer ${this.config.apiKey}`;
        }

        const response = await fetch(this.config.endpoint, {
            method: 'POST',
            headers,
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            throw new Error(`Database API error: ${response.status} ${response.statusText}`);
        }

        logger.debug('Database provider: Successfully sent batch to database');
    }

    public async destroy(): Promise<void> {
        // Flush any remaining events before destroying
        if (this.eventBatch.length > 0) {
            try {
                await this.flushBatch();
            } catch (error) {
                logger.error('Database provider: Failed to flush events during destroy:', error);
            }
        }

        // Clear flush timer
        if (this.flushTimer) {
            clearTimeout(this.flushTimer);
            this.flushTimer = null;
        }

        await super.destroy();
    }

    public updateConfig(newConfig: Partial<DatabaseConfig>): void {
        this.config = { ...this.config, ...newConfig };
        logger.debug('Database provider: Config updated:', this.config);
    }

    public getConfig(): DatabaseConfig {
        return { ...this.config };
    }

    public getPendingEventsCount(): number {
        return this.eventBatch.length;
    }

    public async flushPendingEvents(): Promise<void> {
        await this.flushBatch();
    }
}
